import sodium from "libsodium-wrappers-sumo";
import type { DeviceIdentity, PlainMessage, WireMessage } from "./types";

const variant = () => sodium.base64_variants.URLSAFE_NO_PADDING;
const encode = (value: Uint8Array): string => sodium.to_base64(value, variant());
const decode = (value: string): Uint8Array => sodium.from_base64(value, variant());

export async function cryptoReady(): Promise<void> {
  await sodium.ready;
}

export async function createRoomKey(): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
}

export async function sealRoomKey(roomKey: Uint8Array, publicKey: string): Promise<string> {
  await sodium.ready;
  return encode(sodium.crypto_box_seal(roomKey, decode(publicKey)));
}

export async function openRoomKey(envelope: string, identity: DeviceIdentity): Promise<Uint8Array> {
  await sodium.ready;
  const key = sodium.crypto_box_seal_open(
    decode(envelope),
    decode(identity.encryptionPublicKey),
    decode(identity.encryptionPrivateKey),
  );
  if (!key) throw new Error("Не удалось открыть ключ комнаты");
  return key;
}

function messageAad(message: Pick<WireMessage, "id" | "chatId" | "epoch" | "kind" | "senderDeviceId" | "clientCreatedAt">): string {
  return JSON.stringify({
    v: 1,
    id: message.id,
    chatId: message.chatId,
    epoch: message.epoch,
    kind: message.kind,
    senderDeviceId: message.senderDeviceId,
    clientCreatedAt: message.clientCreatedAt,
  });
}

function signatureBytes(aad: string, nonce: string, ciphertext: string): Uint8Array {
  return sodium.from_string(`${aad}.${nonce}.${ciphertext}`);
}

export async function encryptRoomMessage(
  plain: PlainMessage,
  roomKey: Uint8Array,
  identity: DeviceIdentity,
  metadata: {
    chatId: string;
    clientCreatedAt?: string;
    epoch: number;
    forwardedFrom?: string | null;
    id?: string;
    kind: "text" | "file" | "system";
    mentionUserIds?: string[];
    replyTo?: string | null;
    senderId: string;
  },
): Promise<WireMessage> {
  await sodium.ready;
  const base = {
    id: metadata.id ?? crypto.randomUUID(),
    chatId: metadata.chatId,
    senderId: metadata.senderId,
    senderDeviceId: identity.id,
    epoch: metadata.epoch,
    kind: metadata.kind,
    replyTo: metadata.replyTo ?? null,
    forwardedFrom: metadata.forwardedFrom ?? null,
    mentionUserIds: metadata.mentionUserIds ?? [],
    clientCreatedAt: metadata.clientCreatedAt ?? new Date().toISOString(),
  } as const;
  const aad = messageAad(base);
  const nonceBytes = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    sodium.from_string(JSON.stringify(plain)),
    sodium.from_string(aad),
    null,
    nonceBytes,
    roomKey,
  );
  const nonce = encode(nonceBytes);
  const ciphertext = encode(encrypted);
  const signature = encode(sodium.crypto_sign_detached(
    signatureBytes(aad, nonce, ciphertext),
    decode(identity.signingPrivateKey),
  ));
  return {
    ...base,
    ciphertext,
    nonce,
    signature,
    signingPublicKey: identity.signingPublicKey,
  };
}

export async function decryptRoomMessage(message: WireMessage, roomKey: Uint8Array): Promise<PlainMessage> {
  await sodium.ready;
  const aad = messageAad(message);
  const valid = sodium.crypto_sign_verify_detached(
    decode(message.signature),
    signatureBytes(aad, message.nonce, message.ciphertext),
    decode(message.signingPublicKey),
  );
  if (!valid) throw new Error("INVALID_SIGNATURE");
  const decrypted = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    decode(message.ciphertext),
    sodium.from_string(aad),
    decode(message.nonce),
    roomKey,
  );
  return JSON.parse(sodium.to_string(decrypted)) as PlainMessage;
}

export async function encryptAttachment(file: File): Promise<{
  ciphertext: Blob;
  key: string;
  nonce: string;
  sha256: string;
}> {
  await sodium.ready;
  const key = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES);
  const nonce = sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES);
  const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    new Uint8Array(await file.arrayBuffer()),
    sodium.from_string("JTUC-FILE-V1"),
    null,
    nonce,
    key,
  );
  const encryptedBuffer = Uint8Array.from(encrypted).buffer;
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", encryptedBuffer));
  return {
    ciphertext: new Blob([encryptedBuffer], { type: "application/octet-stream" }),
    key: encode(key),
    nonce: encode(nonce),
    sha256: [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join(""),
  };
}

export async function decryptAttachment(ciphertext: ArrayBuffer, key: string, nonce: string): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    new Uint8Array(ciphertext),
    sodium.from_string("JTUC-FILE-V1"),
    decode(nonce),
    decode(key),
  );
}

export const ATTACHMENT_CHUNK_BYTES = 1024 * 1024;

function chunkNonce(prefix: Uint8Array, index: number): Uint8Array {
  const nonce = new Uint8Array(24);
  nonce.set(prefix, 0);
  new DataView(nonce.buffer).setBigUint64(16, BigInt(index), false);
  return nonce;
}

export async function createAttachmentV2Context(): Promise<{ key: string; nonce: string }> {
  await sodium.ready;
  return {
    key: encode(sodium.randombytes_buf(sodium.crypto_aead_xchacha20poly1305_ietf_KEYBYTES)),
    nonce: encode(sodium.randombytes_buf(16)),
  };
}

export async function encryptAttachmentChunk(
  bytes: ArrayBuffer,
  key: string,
  noncePrefix: string,
  index: number,
  chunkCount: number,
): Promise<{ ciphertext: Blob; sha256: string }> {
  await sodium.ready;
  const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
    new Uint8Array(bytes),
    sodium.from_string(`JTUC-FILE-V2:${index}:${chunkCount}`),
    null,
    chunkNonce(decode(noncePrefix), index),
    decode(key),
  );
  const buffer = Uint8Array.from(encrypted).buffer;
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", buffer));
  return { ciphertext: new Blob([buffer], { type: "application/octet-stream" }), sha256: [...digest].map((byte) => byte.toString(16).padStart(2, "0")).join("") };
}

export async function decryptAttachmentChunk(
  ciphertext: ArrayBuffer,
  key: string,
  noncePrefix: string,
  index: number,
  chunkCount: number,
): Promise<Uint8Array> {
  await sodium.ready;
  return sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
    null,
    new Uint8Array(ciphertext),
    sodium.from_string(`JTUC-FILE-V2:${index}:${chunkCount}`),
    chunkNonce(decode(noncePrefix), index),
    decode(key),
  );
}
